/**
 * GardEn shared motion language (the "house style").
 *
 * Single source of truth for every animation timing, easing, stagger, and
 * press constant across apps/web. Extracted verbatim from the shipped /chat
 * revamp (apps/web/src/features/chat/{MessageList,ChatRoute,MessageInput}.tsx)
 * so screens import these instead of re-declaring inline literals (which is how
 * timings drift and the reduced-motion gate gets forgotten).
 *
 * Rules baked into the vocabulary:
 *   - Enter eases out; exits run faster than enters; never a spring/bounce
 *     (except the AppShell shared-layout indicators, which use their own
 *     spring local to that file).
 *   - Animate transform + opacity ONLY (CSP-safe; no layout thrash).
 *   - Every consumer gates on prefers-reduced-motion — see `useGardenMotion`.
 */
import type { MotionProps, Transition, Variants } from "framer-motion";

/** Canonical durations (s), easings, stagger + press scales. */
export const MOTION = {
  /** Durations in seconds. */
  dur: {
    token: 0.05,
    press: 0.12,
    screen: 0.14,
    nav: 0.15,
    bubble: 0.22,
    item: 0.28,
    slideOver: 0.3,
  },
  ease: {
    /** THE default enter curve for every entrance/reveal. */
    out: "easeOut" as const,
    /** ONLY non-easeOut curve — slide-overs / sheets / drawers. */
    standard: [0.2, 0.7, 0.3, 1] as [number, number, number, number],
  },
  stagger: {
    /** Delay between consecutive children in a small first-paint group. */
    children: 0.045,
    /** Initial delay before the first staggered child begins. */
    delay: 0.03,
  },
  press: {
    /** Cards / surfaces. */
    surface: 0.98,
    /** Icon / CTA buttons. */
    cta: 0.95,
    /** Mobile bottom-tab icon (scoped to the icon). */
    navTab: 0.92,
  },
};

/** Parent of a small first-paint group; orchestrates the stagger. */
export const containerVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: MOTION.stagger.children,
      delayChildren: MOTION.stagger.delay,
    },
  },
};

/** A staggered child's fade + rise. */
export const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: MOTION.dur.item, ease: MOTION.ease.out },
  },
};

/**
 * Fade + rise for a single fresh/local element (a hero band, a conditionally
 * mounted notice). Returns no motion props when `animate` is false so persisted
 * content renders statically (the chat "only animate fresh content" rule).
 */
export function bubbleMotion(animate: boolean): Record<string, unknown> {
  return animate
    ? {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: MOTION.dur.bubble, ease: MOTION.ease.out },
      }
    : {};
}

/** Backdrop scrim fade for overlays/sheets. Exit faster than enter. */
export const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: MOTION.dur.nav } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

/**
 * Slide-over / sheet / drawer panel entrance along an axis. `from` is the
 * offset it slides in from (e.g. 16 for a small rise, or a panel width).
 * Uses the only non-easeOut curve; exit runs faster.
 */
export function slideOverVariants(axis: "x" | "y", from: number): Variants {
  // Build concrete x/y keys (a computed `[axis]` key widens to a string index
  // signature that framer's Variant type rejects).
  const offset = axis === "x" ? { x: from } : { y: from };
  const settled = axis === "x" ? { x: 0 } : { y: 0 };
  return {
    hidden: { opacity: 0, ...offset },
    show: {
      opacity: 1,
      ...settled,
      transition: { duration: MOTION.dur.slideOver, ease: MOTION.ease.standard },
    },
    exit: {
      opacity: 0,
      ...offset,
      transition: { duration: 0.2, ease: MOTION.ease.out },
    },
  };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Shell + nav additions (2026-08 counter revamp). Everything above this line
 * keeps its exact name and shape — these are additive only.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The shared-layout spring behind every sliding active-tab indicator (the
 * desktop tab underline, the mobile bottom-bar rule). It lived as a private
 * const inside AppShell; it is here now because the shell is no longer the
 * only surface with a `layoutId` indicator, and two copies of a spring is how
 * two navs end up moving at different speeds.
 *
 * This is the ONE spring in the vocabulary. Everything else eases out — a
 * shared-layout element is the exception because it is a physical object
 * moving between two places, not content arriving.
 */
export const INDICATOR_SPRING: Transition = {
  type: "spring",
  stiffness: 380,
  damping: 32,
};

/**
 * Framer's layout animation is NOT auto-disabled by prefers-reduced-motion —
 * a `layoutId` element keeps sliding unless the transition says otherwise. So
 * every indicator call site passes this instead of the spring directly, and
 * the indicator JUMPS under reduced motion rather than travelling.
 */
export function indicatorTransition(reduced: boolean): Transition {
  return reduced ? { duration: 0 } : INDICATOR_SPRING;
}

/**
 * Dropdown / popover panel entrance — the account menu, the language menu,
 * the search history. A 4px drop with no scale: a menu is a sheet of paper
 * coming off a pad, not something zooming in.
 */
export const menuVariants: Variants = {
  hidden: { opacity: 0, y: -4 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: MOTION.dur.nav, ease: MOTION.ease.out },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: MOTION.dur.press, ease: MOTION.ease.out },
  },
};

/**
 * Directional route transition for the shell's routed region.
 *
 * A pure cross-fade told the user that something changed but not which way
 * they moved; with four tabs in a fixed order, the direction is real
 * information the nav already carries. `direction` is -1 (moved left in the
 * tab order), 1 (right), or 0 (no order relationship — e.g. a deep link, or a
 * route outside the tab set), which degrades to the plain fade.
 *
 * Enter only. The exit stays a straight fade so the outgoing screen never
 * appears to be shoved: under `mode="wait"` the two never overlap, and an
 * exit offset just reads as a jitter. Transform + opacity only; the host
 * region clips its x-overflow so the offset can never produce a scrollbar.
 */
export function routeTransition(direction: number, reduced: boolean): MotionProps {
  if (reduced) return {};
  const offset = direction === 0 ? 0 : direction * 12;
  return {
    initial: { opacity: 0, x: offset },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0 },
    transition: { duration: MOTION.dur.screen, ease: MOTION.ease.out },
  };
}
